
import {
  defineComponent, PropType, ref, watch,
} from 'vue';
import draggable from 'vuedraggable';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import {
  FieldConfig,
  ParserFieldObject,
  Property,
} from '@/types';
import { useI18n } from 'vue-i18n';

interface MenuItem {
  key: string;
  label: string;
  url: string;
}

export default defineComponent({
  name: 'MenuInputGroup',

  components: {
    draggable,
    InputText,
    Button,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },
    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const property = ref<Property>(props.parserValues.properties[0]);

    const menuItems = ref<MenuItem[]>([]);

    const initializeMenuItems = () => {
      const items = property.value.value || [];
      menuItems.value = items.map((item: any) => ({
        key: item.key || `spm_menu_item_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
        label: item.label || '',
        url: item.url || '',
      }));
    };

    const generateUniqueKey = (): string => {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 8);
      return `spm_menu_item_${timestamp}_${random}`;
    };

    const emitChanges = () => {
      const formattedValue = menuItems.value.map((item) => ({
        key: item.key,
        label: item.label,
        url: item.url,
      }));

      property.value.value = formattedValue;
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [property.value],
      });
    };

    const handleAddItem = () => {
      const newItem: MenuItem = {
        key: generateUniqueKey(),
        label: `Menu ${menuItems.value.length + 1}`,
        url: '{var=shop.url}',
      };
      menuItems.value.push(newItem);
      emitChanges();
    };

    const handleRemoveItem = (index: number) => {
      menuItems.value.splice(index, 1);
      emitChanges();
    };

    const handleLabelChange = (index: number) => {
      emitChanges();
    };

    const handleUrlChange = (index: number) => {
      emitChanges();
    };

    const handleOrderChange = () => {
      emitChanges();
    };

    watch(() => props.parserValues.properties[0], () => {
      property.value = props.parserValues.properties[0];
      initializeMenuItems();
    }, { immediate: true, deep: true });

    return {
      t,
      menuItems,
      handleAddItem,
      handleRemoveItem,
      handleLabelChange,
      handleUrlChange,
      handleOrderChange,
    };
  },
});
